/**
 * Wishlist Manager
 * Handles localStorage persistence, heart button UI state,
 * and provides a full API for the wishlist page renderer.
 *
 * localStorage key: 'wishlist_items'
 * Data shape per item:
 * {
 *   id: number,
 *   variantId: number,
 *   title: string,
 *   url: string,
 *   image: string,
 *   price: string,
 *   comparePrice: string,
 *   badge: string,
 *   vendor: string,
 *   productType: string,
 *   available: boolean,
 *   handle: string,
 *   addedAt: string (ISO timestamp)
 * }
 */

const WISHLIST_KEY = 'wishlist_items';

/* ─── Core Storage API ─── */

const Wishlist = {

  /**
   * Get all wishlist items
   * @returns {Array<Object>}
   */
  getAll() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch {
      return [];
    }
  },

  /**
   * Save items array back to localStorage
   * @param {Array<Object>} items
   */
  save(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { items, count: items.length }
      }));
    } catch (e) {
      console.warn('Wishlist: localStorage write failed', e);
    }
  },

  /**
   * Check if a product is wishlisted
   * @param {number|string} productId
   * @returns {boolean}
   */
  has(productId) {
    return this.getAll().some(item => String(item.id) === String(productId));
  },

  /**
   * Add a product to wishlist
   * @param {Object} productData
   */
  add(productData) {
    const items = this.getAll();
    const exists = items.some(item => String(item.id) === String(productData.id));
    if (exists) return;
    items.push({
      ...productData,
      addedAt: new Date().toISOString()
    });
    this.save(items);
  },

  /**
   * Remove a product from wishlist by productId
   * @param {number|string} productId
   */
  remove(productId) {
    const items = this.getAll().filter(
      item => String(item.id) !== String(productId)
    );
    this.save(items);
  },

  /**
   * Toggle a product in/out of wishlist
   * @param {Object} productData
   * @returns {boolean} true if now wishlisted, false if removed
   */
  toggle(productData) {
    if (this.has(productData.id)) {
      this.remove(productData.id);
      return false;
    } else {
      this.add(productData);
      return true;
    }
  },

  /**
   * Get total count
   * @returns {number}
   */
  count() {
    return this.getAll().length;
  },

  /**
   * Clear all wishlist items
   */
  clear() {
    this.save([]);
  }
};


/* ─── Heart Button Controller ─── */

class WishlistHeartButton {

  /**
   * @param {HTMLButtonElement} btn
   */
  constructor(btn) {
    this.btn = btn;
    this.productId = btn.dataset.wishlistId;
    this.productData = this.#extractProductData(btn);

    // Set initial state from localStorage
    this.#syncState();

    // Click handler
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#toggle();
    });

    // Listen for external wishlist changes (e.g. from wishlist page removal)
    window.addEventListener('wishlist:updated', () => this.#syncState());
  }

  /**
   * Extract all product data from button data attributes
   * @param {HTMLButtonElement} btn
   * @returns {Object}
   */
  #extractProductData(btn) {
    return {
      id:          btn.dataset.wishlistId,
      variantId:   btn.dataset.wishlistVariantId,
      title:       btn.dataset.wishlistTitle,
      url:         btn.dataset.wishlistUrl,
      image:       btn.dataset.wishlistImage,
      price:       btn.dataset.wishlistPrice,
      comparePrice: btn.dataset.wishlistComparePrice || null,
      badge:       btn.dataset.wishlistBadge || null,
      vendor:      btn.dataset.wishlistVendor || null,
      productType: btn.dataset.wishlistProductType || null,
      available:   btn.dataset.wishlistAvailable === 'true',
      handle:      btn.dataset.wishlistHandle,
    };
  }

  /**
   * Sync button visual state with localStorage
   */
  #syncState() {
    const isWishlisted = Wishlist.has(this.productId);
    this.btn.dataset.wishlisted = String(isWishlisted);
    this.btn.setAttribute('aria-label',
      isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'
    );
    this.btn.setAttribute('aria-pressed', String(isWishlisted));
  }

  /**
   * Toggle wishlist state with pop animation
   */
  #toggle() {
    const nowWishlisted = Wishlist.toggle(this.productData);

    // Pop animation
    this.btn.removeAttribute('data-heart-pop');
    // Force reflow to restart animation
    void this.btn.offsetWidth;
    this.btn.dataset.heartPop = 'true';
    setTimeout(() => this.btn.removeAttribute('data-heart-pop'), 350);

    // Update aria immediately (syncState also fires via wishlist:updated event)
    this.btn.dataset.wishlisted = String(nowWishlisted);
    this.btn.setAttribute('aria-label',
      nowWishlisted ? 'Remove from wishlist' : 'Add to wishlist'
    );
    this.btn.setAttribute('aria-pressed', String(nowWishlisted));
  }
}


/* ─── Auto-init all heart buttons on the page ─── */

function initWishlistButtons() {
  document.querySelectorAll('.quick-add__heart-btn[data-wishlist-id]').forEach(btn => {
    if (!btn._wishlistInit) {
      btn._wishlistInit = true;
      new WishlistHeartButton(btn);
    }
  });
}

// Init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlistButtons);
} else {
  initWishlistButtons();
}

// Re-init after Shopify Section Rendering API swaps innerHTML
// (product-recommendations.js swaps innerHTML after lazy fetch)
const wishlistObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'childList' && mutation.addedNodes.length) {
      const hasNewHearts = [...mutation.addedNodes].some(node =>
        node.nodeType === 1 && (
          node.classList?.contains('quick-add__heart-btn') ||
          node.querySelector?.('.quick-add__heart-btn')
        )
      );
      if (hasNewHearts) initWishlistButtons();
    }
  }
});

wishlistObserver.observe(document.body, {
  childList: true,
  subtree: true
});


/* ─── Wishlist Page Renderer ─── */

/**
 * Call this on your wishlist page template to render saved items.
 *
 * Usage in wishlist page Liquid:
 *   <div id="wishlist-grid" class="wishlist-grid"></div>
 *   <p id="wishlist-empty" class="wishlist-empty hidden">Your wishlist is empty.</p>
 *   <script src="{{ 'wishlist.js' | asset_url }}" type="module"></script>
 *
 * @param {string} containerId - ID of the grid container div
 * @param {string} emptyId - ID of the empty state element
 */
function renderWishlistPage(containerId = 'wishlist-grid', emptyId = 'wishlist-empty') {
  const container = document.getElementById(containerId);
  const emptyEl   = document.getElementById(emptyId);
  if (!container) return;

  const items = Wishlist.getAll();

  if (!items.length) {
    container.innerHTML = '';
    emptyEl?.classList.remove('hidden');
    return;
  }

  emptyEl?.classList.add('hidden');

  container.innerHTML = items.map(item => `
    <div class="wishlist-card" data-product-id="${item.id}">

      <div class="wishlist-card__media">
        <a href="${item.url}" class="wishlist-card__image-link">
          <img
            src="${item.image}"
            alt="${item.title}"
            class="wishlist-card__image"
            loading="lazy"
            width="600"
          >
        </a>

        ${item.badge ? `
          <div class="wishlist-card__badge">${item.badge}</div>
        ` : ''}

        <button
          class="wishlist-card__remove-btn"
          type="button"
          aria-label="Remove ${item.title} from wishlist"
          data-remove-id="${item.id}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="#e53e3e" stroke="#e53e3e"/>
          </svg>
        </button>
      </div>

      <div class="wishlist-card__info">
        <a href="${item.url}" class="wishlist-card__title">${item.title}</a>
        ${item.vendor ? `<p class="wishlist-card__vendor">${item.vendor}</p>` : ''}
        <div class="wishlist-card__price">
          ${item.comparePrice ? `
            <span class="wishlist-card__price--sale">${item.price}</span>
            <span class="wishlist-card__price--compare">${item.comparePrice}</span>
          ` : `
            <span class="wishlist-card__price--regular">${item.price}</span>
          `}
        </div>
        ${item.available === false ? `
          <p class="wishlist-card__unavailable">Sold Out</p>
        ` : ''}
      </div>

    </div>
  `).join('');

  // Bind remove buttons
  container.querySelectorAll('.wishlist-card__remove-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.removeId;
      Wishlist.remove(id);

      // Animate card out
      const card = btn.closest('.wishlist-card');
      card.style.transition = 'opacity 0.25s ease, scale 0.25s ease';
      card.style.opacity = '0';
      card.style.scale = '0.95';
      setTimeout(() => {
        card.remove();
        if (!container.children.length) {
          emptyEl?.classList.remove('hidden');
        }
      }, 260);
    });
  });
}


/* ─── Wishlist Count Badge Updater ─── */

/**
 * Updates any element with [data-wishlist-count] attribute
 * with the current wishlist item count.
 * Add data-wishlist-count to your header wishlist icon span.
 */
function updateWishlistCountBadges() {
  const count = Wishlist.count();
  document.querySelectorAll('[data-wishlist-count]').forEach(el => {
    el.textContent = count > 0 ? String(count) : '';
    el.style.display = count > 0 ? '' : 'none';
  });
}

updateWishlistCountBadges();
window.addEventListener('wishlist:updated', updateWishlistCountBadges);


/* ─── Export for use in other scripts or wishlist page ─── */
window.Wishlist = Wishlist;
window.renderWishlistPage = renderWishlistPage;
